import './config/habitat/conditions'; // registers the habitat condition predicates

import { guidedJourneyConfig } from './config/guidedJourney';
import { ASLEEP_FIRST_STEP_ID, habitatAsleepConfig } from './config/habitat/asleep';
import {
  HABITAT_ONBOARDING_ASLEEP_ID,
  HABITAT_ONBOARDING_ONLINE_ID,
  HABITAT_ONBOARDING_OUTAGE_ID,
} from './config/habitat/ids';
import { habitatOnlineConfig, ONLINE_FIRST_STEP_ID } from './config/habitat/online';
import { habitatOutageConfig, OUTAGE_FIRST_STEP_ID } from './config/habitat/outage';
import { tzPlusConfig } from './config/tzPlus';
import { hasTzPlusFeature } from './config/tzPlus/feature';
import { TZ_PLUS_ANALYTICS_NAME, TZ_PLUS_FIRST_STEP_ID, TZ_PLUS_ID } from './config/tzPlus/ids';
import { isCompeting } from './core/arbitrate';
import { evaluateCond } from './core/conditions';
import { route } from './core/route';
import { isCleared } from './core/status';
import { CondResult, TourConfig, TourContext, TourGateState, TourRegistration, TourStep } from './types';

export const GUIDED_JOURNEY_ID = 'guidedJourney';

export { HABITAT_ONBOARDING_IDS } from './config/habitat/ids';

// The same question as selectHasGuidedExperienceFeature, asked of the gate so the registry does
// not drag the selectors module into tour tests.
const hasGuidedExperience = (gate: TourGateState): boolean => gate.can('tour.default');

/**
 * The Toronto Zoo Plus tour. Ten stops at one zoo, gated on its own entitlement.
 *
 * Nothing holds it back: when a user has both passes this is the one that runs first, and the
 * guided journey below waits for it. See docs/adr/0012.
 */
const tzPlus: TourRegistration = {
  id: TZ_PLUS_ID,
  name: TZ_PLUS_ANALYTICS_NAME,
  config: tzPlusConfig,
  scope: 'global',
  exclusive: true,
  priority: 100,
  firstStepId: TZ_PLUS_FIRST_STEP_ID,
  isEligible: hasTzPlusFeature,
  autoStart: (_gate, pathname) => pathname === '/home',
};

const guidedJourney: TourRegistration = {
  id: GUIDED_JOURNEY_ID,
  name: 'All Access Guided Tour', // pre-existing analytics name
  config: guidedJourneyConfig,
  scope: 'global',
  exclusive: true,
  priority: 100,
  firstStepId: '0-modal',
  /**
   * The two passes are sold separately, so a user can hold both — and then the tours run in order
   * rather than at once. Decided here rather than by priority because `TourHost` starts EVERY tour
   * passing `shouldAutoStart` before the arbiter picks one to draw: equal priorities would leave
   * both with a `startedAt` and a `triggered` event, and the tie would then resolve by position in
   * the TOURS array. `isCleared` rather than `completedAt` so a skip, if one is ever added to TZ+,
   * releases this without a change here. See docs/adr/0012.
   *
   * It waits only for a TZ+ tour that is actually IN the queue. Somebody holding the All-Access
   * pass alone has no TZ+ record, and an absent record is `notStarted`, never cleared — so without
   * the entitlement check every existing All-Access customer would wait forever for a tour they
   * cannot start.
   */
  isEligible: (gate) => hasGuidedExperience(gate) && (!hasTzPlusFeature(gate) || isCleared(gate.tours[TZ_PLUS_ID])),
  // Auto-starts for an eligible user landing on /home.
  autoStart: (_gate, pathname) => pathname === '/home',
};

/**
 * Is one of the exclusive journeys holding the screen? The habitat onboardings wait for whichever
 * is running rather than competing with it — two guides talking at once helps nobody.
 *
 * Asking `deriveStatus(...) === 'active'` alone would be a bug. A user whose feature lapses
 * mid-journey keeps a stepHistory with no completedAt/skippedAt, so the journey reads `active`
 * forever — and habitat onboarding would be blocked permanently. `isEligible` is what actually
 * retires it, so the shared `isCompeting` (the arbiter's own candidate test) is the only correct
 * question.
 *
 * BOTH journeys are asked, not just the guided one. `exclusive: true` stops the onboardings being
 * DRAWN behind a running TZ+ tour, but not from STARTING: `TourHost` starts every tour whose gate
 * passes, so as TZ+ walked the user through ten habitat pages each scenario onboarding would stamp
 * a `startedAt`, emit a `triggered`, and then surface mid-flow once TZ+ ended.
 */
const journeyHoldsTheScreen = (gate: TourGateState, ctx: TourContext | undefined, pathname: string): boolean =>
  [tzPlus, guidedJourney].some((journey) => isCompeting(journey, gate, ctx, pathname));

/**
 * One registration per habitat scenario. Three tours rather than one because each has to keep
 * its own position: leaving an online habitat mid-flow for an offline one, then coming back,
 * should resume where the user left off in BOTH. Separate `stepHistory` per tour gives that for
 * free, and only one is ever eligible, so they never compete.
 *
 * There is no bespoke `autoStart`: eligibility answers `pending` until the habitat page has
 * published its context, and starting waits for a definite `true`.
 */
const habitatOnboarding = (id: string, config: TourConfig, firstStepId: string, cond: string): TourRegistration => ({
  id,
  name: 'Habitat Onboarding', // one analytics identity across the three scenarios
  config,
  scope: route('/h/*/*'),
  exclusive: false,
  // Below the journeys (100), which are exclusive anyway.
  priority: 50,
  firstStepId,
  isEligible: (gate, ctx, pathname): CondResult => {
    if (journeyHoldsTheScreen(gate, ctx, pathname)) return false;
    // A modal is holding the screen: step aside (not `pending` — a modal is a definite state)
    // and resume at the same step once it closes.
    if (ctx?.blockingModalOpen) return false;
    return evaluateCond(cond, ctx);
  },
  autoStart: () => true,
});

const habitatOnboardings: TourRegistration[] = [
  habitatOnboarding(HABITAT_ONBOARDING_ONLINE_ID, habitatOnlineConfig, ONLINE_FIRST_STEP_ID, 'habitatOnline'),
  habitatOnboarding(HABITAT_ONBOARDING_ASLEEP_ID, habitatAsleepConfig, ASLEEP_FIRST_STEP_ID, 'habitatAsleep'),
  habitatOnboarding(HABITAT_ONBOARDING_OUTAGE_ID, habitatOutageConfig, OUTAGE_FIRST_STEP_ID, 'habitatOutage'),
];

/**
 * The tour registry. Adding a tour = one entry here + an authored config array.
 * `scope` + `exclusive` + `priority` drive the arbiter (see useTourArbiter).
 */
export const TOURS: TourRegistration[] = [tzPlus, guidedJourney, ...habitatOnboardings];

const TOUR_BY_ID = new Map(TOURS.map((t) => [t.id, t]));

export const getTour = (tourId: string): TourRegistration | undefined => TOUR_BY_ID.get(tourId);

// id -> step lookup per tour, built once at module load. The entries are references to the
// authored step objects, so this costs a couple of Maps and no duplication. The config array
// stays the source of truth (order included); no separate graph structure is needed.
const STEPS_BY_TOUR = new Map(TOURS.map((tour) => [tour.id, new Map(tour.config.steps.map((step) => [step.id, step]))]));

export const getStep = (tourId: string, stepId: string): TourStep | undefined => STEPS_BY_TOUR.get(tourId)?.get(stepId);
